import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, Button, IconButton, Tooltip, Slider,
  Menu, MenuItem, FormControl, Select, Divider,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import DeleteIcon from '@material-ui/icons/Delete';
import LayersIcon from '@material-ui/icons/Layers';
import ImageIcon from '@material-ui/icons/Image';
import DescriptionIcon from '@material-ui/icons/Description';
import { OVERLAY_CATEGORIES, OVERLAY_SUB_TYPES } from '../../overlayConstants';
import PlanPagePickerModal from '../PlanPagePickerModal';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  section: {
    marginBottom: 8,
  },
  uploadBtn: {
    width: '100%',
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    background: '#EEF2FF',
    color: '#4F46E5',
    border: '1px solid #C7D2FE',
    '&:hover': { background: '#E0E7FF' },
  },
  masterOpacity: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    background: '#fff8e1',
    borderRadius: 6,
    marginBottom: 12,
  },
  masterLabel: {
    fontSize: 11,
    color: '#b45309',
    fontWeight: 600,
    minWidth: 70,
  },
  overlayCard: {
    padding: 10,
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 8,
    transition: 'all 0.15s ease',
  },
  overlayCardActive: {
    borderColor: '#ff9800',
    background: '#fff8e1',
    borderLeftWidth: 3,
  },
  overlayHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  overlayName: {
    flex: 1,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  overlayNameActive: {
    fontWeight: 700,
    color: '#e65100',
  },
  categoryRow: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
    paddingLeft: 4,
  },
  categorySelect: {
    flex: 1,
    '& .MuiOutlinedInput-input': {
      fontSize: 11,
      padding: '4px 8px',
    },
  },
  opacityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
    paddingLeft: 4,
  },
  emptyState: {
    padding: 24,
    textAlign: 'center',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px dashed #ddd',
  },
  projectCard: {
    padding: 12,
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    marginBottom: 8,
  },
  planPagesBtn: {
    width: '100%',
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    background: '#E8F5E9',
    color: '#2e7d32',
    border: '1px solid #A5D6A7',
    marginTop: 8,
    '&:hover': { background: '#C8E6C9' },
  },
}));

const getOverlayName = (ov, idx) => {
  const url = ov.imageUrl || '';
  const fileName = url.split('/').pop()?.replace(/^[a-f0-9]+_/, '')?.replace(/\.[^.]+$/, '');
  return fileName || `Overlay ${idx + 1}`;
};

const ProjectsMode = ({
  url,
  jobsite,
  overlays = [],
  activeOverlayId,
  uploading,
  savedOverlayUrls = [],
  onUploadFile,
  onAddExistingOverlay,
  onSetVisible,
  onSetOpacity,
  onSetMasterOpacity,
  onSetLocked,
  onSetCategory,
  onActivate,
  onRemove,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [overlayMenuAnchor, setOverlayMenuAnchor] = useState(null);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);

  const handlePlanPagesSelected = (selectedPages) => {
    // Each selected page becomes an overlay via its imageUrl
    selectedPages.forEach((page) => {
      if (page.imageUrl && onAddExistingOverlay) {
        onAddExistingOverlay(page.imageUrl);
      }
    });
  };

  const handleUploadClick = (e) => {
    if (savedOverlayUrls.length > 0) {
      setOverlayMenuAnchor(e.currentTarget);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onUploadFile) onUploadFile(file);
  };

  return (
    <div className={classes.root}>
      {/* Past projects placeholder (bid estimation feature coming later) */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Past Projects</Typography>
        <div className={classes.projectCard}>
          <Typography variant="body2" style={{ fontWeight: 500, fontSize: 13 }}>
            No past projects yet
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Bid estimation will track projects here. Coming soon.
          </Typography>
        </div>
      </div>

      <Divider />

      {/* Plan Overlays */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Plan Overlays {overlays.length > 0 && `(${overlays.length})`}
        </Typography>

        <Button
          className={classes.uploadBtn}
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={handleUploadClick}
          disabled={!jobsite?._id || uploading}
          data-tour="site-takeoff-projects-upload"
        >
          {uploading ? 'Uploading...' : overlays.length ? 'Add another overlay' : 'Upload plan overlay'}
        </Button>
        <Button
          className={classes.planPagesBtn}
          variant="outlined"
          size="small"
          startIcon={<DescriptionIcon />}
          onClick={() => setPlanPickerOpen(true)}
          disabled={!jobsite?._id}
        >
          Add from plan set
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Menu
          anchorEl={overlayMenuAnchor}
          open={Boolean(overlayMenuAnchor)}
          onClose={() => setOverlayMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setOverlayMenuAnchor(null);
              fileInputRef.current?.click();
            }}
          >
            <CloudUploadIcon fontSize="small" style={{ marginRight: 8 }} />
            Upload new image
          </MenuItem>
          <Divider />
          {savedOverlayUrls.map((overlayUrl, idx) => {
            const shortName = overlayUrl.split('/').pop()?.replace(/^[a-f0-9]+_/, '');
            return (
              <MenuItem
                key={overlayUrl}
                onClick={() => {
                  setOverlayMenuAnchor(null);
                  onAddExistingOverlay?.(overlayUrl);
                }}
              >
                <ImageIcon fontSize="small" style={{ marginRight: 8, color: '#666' }} />
                <span style={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {shortName || `Overlay ${idx + 1}`}
                </span>
              </MenuItem>
            );
          })}
        </Menu>

        {overlays.length >= 2 && (
          <div className={classes.masterOpacity} style={{ marginTop: 12 }}>
            <span className={classes.masterLabel}>All Opacity</span>
            <Slider
              value={overlays[0]?.opacity ?? 0.6}
              onChange={(e, val) => onSetMasterOpacity(val)}
              min={0}
              max={1}
              step={0.05}
              size="small"
              style={{ flex: 1, color: '#ff9800' }}
            />
          </div>
        )}

        {overlays.length === 0 ? (
          <div className={classes.emptyState} style={{ marginTop: 12 }}>
            <LayersIcon style={{ fontSize: 32, color: '#bbb', marginBottom: 4 }} />
            <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
              No overlays yet
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', fontSize: 10 }}>
              PNG or JPG of plans, aerials, or sketches
            </Typography>
          </div>
        ) : (
          <Box mt={2}>
            {overlays.map((ov, idx) => {
              const fileName = getOverlayName(ov, idx);
              const isActive = activeOverlayId === ov.id;
              const subTypes = ov.category ? (OVERLAY_SUB_TYPES[ov.category] || []) : [];
              return (
                <div
                  key={ov.id}
                  className={`${classes.overlayCard} ${isActive ? classes.overlayCardActive : ''}`}
                >
                  <div className={classes.overlayHeader}>
                    <Tooltip title={ov.visible ? 'Hide' : 'Show'}>
                      <IconButton
                        size="small"
                        onClick={() => onSetVisible(ov.id, !ov.visible)}
                        style={{ padding: 2 }}
                      >
                        {ov.visible
                          ? <VisibilityIcon style={{ fontSize: 14, color: '#ff9800' }} />
                          : <VisibilityOffIcon style={{ fontSize: 14, color: '#999' }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isActive && !ov.locked ? 'Click to lock & deactivate' : 'Click to unlock & edit'}>
                      <Typography
                        className={`${classes.overlayName} ${isActive && !ov.locked ? classes.overlayNameActive : ''}`}
                        onClick={() => {
                          if (isActive && !ov.locked) {
                            // Currently editing — lock it
                            onSetLocked(ov.id, true);
                          } else {
                            // Not editing — unlock and activate
                            onSetLocked(ov.id, false);
                          }
                        }}
                      >
                        {isActive && !ov.locked ? '▶ ' : ''}{fileName}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={ov.locked ? 'Unlock & edit' : 'Lock overlay'}>
                      <IconButton
                        size="small"
                        onClick={() => onSetLocked(ov.id, !ov.locked)}
                        style={{ padding: 2 }}
                      >
                        {ov.locked
                          ? <LockIcon style={{ fontSize: 14, color: '#d32f2f' }} />
                          : <LockOpenIcon style={{ fontSize: 14, color: '#4caf50' }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove overlay">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm(`Remove ${fileName}?`)) onRemove(ov.id);
                        }}
                        style={{ padding: 2 }}
                      >
                        <DeleteIcon style={{ fontSize: 14, color: '#999' }} />
                      </IconButton>
                    </Tooltip>
                  </div>

                  <div className={classes.opacityRow}>
                    <Typography variant="caption" style={{ fontSize: 10, color: '#999', minWidth: 50 }}>
                      Opacity
                    </Typography>
                    <Slider
                      value={ov.opacity ?? 0.6}
                      onChange={(e, val) => onSetOpacity(ov.id, val)}
                      min={0}
                      max={1}
                      step={0.05}
                      size="small"
                      style={{ color: '#ff9800' }}
                      disabled={!ov.visible}
                    />
                  </div>

                  <div className={classes.categoryRow}>
                    <FormControl variant="outlined" size="small" className={classes.categorySelect}>
                      <Select
                        value={ov.category || ''}
                        onChange={(e) => onSetCategory(ov.id, 'category', e.target.value)}
                        displayEmpty
                      >
                        {OVERLAY_CATEGORIES.map(c => (
                          <MenuItem key={c.value} value={c.value} style={{ fontSize: 12 }}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {subTypes.length > 0 && (
                      <FormControl variant="outlined" size="small" className={classes.categorySelect}>
                        <Select
                          value={ov.subType || ''}
                          onChange={(e) => onSetCategory(ov.id, 'subType', e.target.value)}
                          displayEmpty
                        >
                          {subTypes.map(s => (
                            <MenuItem key={s.value} value={s.value} style={{ fontSize: 12 }}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                </div>
              );
            })}
          </Box>
        )}
      </div>

      <PlanPagePickerModal
        open={planPickerOpen}
        onClose={() => setPlanPickerOpen(false)}
        url={url}
        jobsite={jobsite}
        onAddPages={handlePlanPagesSelected}
      />
    </div>
  );
};

export default ProjectsMode;
